import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { selectRandomSponsorAd, SponsorAdDefinition } from '../config/sponsorAds';

type SponsorAdProps = {
    my?: number | { xs?: number; sm?: number; md?: number };
    placement?: string;
};

const sendAdEvent = (
    eventName: 'sponsor_ad_impression' | 'sponsor_ad_click',
    ad: SponsorAdDefinition,
    placement: string,
) => {
    if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', eventName, {
            ad_id: ad.id,
            ad_name: ad.name,
            ad_placement: placement,
            destination_url: ad.href,
        });
    }
};

const SponsorAd: React.FC<SponsorAdProps> = ({ my = 3, placement = 'content_banner' }) => {
    const [ad] = useState(selectRandomSponsorAd);
    const containerRef = useRef<HTMLElement | null>(null);
    const impressionSentRef = useRef(false);

    useEffect(() => {
        const element = containerRef.current;
        if (!element || impressionSentRef.current) return;

        const recordImpression = () => {
            if (impressionSentRef.current) return;
            impressionSentRef.current = true;
            sendAdEvent('sponsor_ad_impression', ad, placement);
        };

        if (!('IntersectionObserver' in window)) {
            recordImpression();
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                    recordImpression();
                    observer.disconnect();
                }
            },
            { threshold: 0.5 },
        );

        observer.observe(element);
        return () => observer.disconnect();
    }, [ad, placement]);

    return (
        <Box
            ref={containerRef}
            component="aside"
            aria-label={`Sponsored advertisement from ${ad.name}`}
            sx={{ my }}
        >
            <Typography
                component="p"
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, textAlign: 'center', letterSpacing: '0.08em', textTransform: 'uppercase' }}
            >
                Sponsored
            </Typography>
            <Box
                component="a"
                href={ad.href}
                target="_blank"
                rel="sponsored noopener noreferrer"
                aria-label={`Visit ${ad.name} (opens in a new tab)`}
                onClick={() => sendAdEvent('sponsor_ad_click', ad, placement)}
                sx={{
                    display: 'block',
                    width: '100%',
                    lineHeight: 0,
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: 1,
                    transition: 'transform 160ms ease, box-shadow 160ms ease',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: 3,
                    },
                    '&:focus-visible': {
                        outline: '3px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: 3,
                    },
                }}
            >
                <picture>
                    {ad.mobileImageSrc && <source media="(max-width: 600px)" srcSet={ad.mobileImageSrc} />}
                    <Box
                        component="img"
                        src={ad.imageSrc}
                        alt={ad.alt}
                        sx={{ display: 'block', width: '100%', height: 'auto' }}
                    />
                </picture>
            </Box>
        </Box>
    );
};

export default SponsorAd;
